import React, { useState, useEffect, useRef, memo, useMemo } from 'react';
import { Html } from '@react-three/drei';
import { useGPUTier } from '../../hooks/useGPUTier';
import { useConditionalFrame } from '../../hooks/useOptimizedFrame';
import { usePreloading } from '../../contexts/SiteContext';
import { useConnectionSpeed } from '../../hooks/useConnectionSpeed';
import { getLogoPath } from '../../utils/logoPath';
import { logger } from '../../utils/logger';

const AnimatedLogos = ({ offsetPosition = [0, 0, -25] }) => {
  const [currentLogoIndex, setCurrentLogoIndex] = useState(0);
  const [opacity, setOpacity] = useState(0);
  const [imagesReady, setImagesReady] = useState(false);
  const logoRef = useRef();
  const imagesRenderedRef = useRef(false); // Track if images have been rendered to prevent re-rendering
  const imgRefsRef = useRef(new Map()); // Store refs to img elements to set src from preloaded images
  const { performanceSettings } = useGPUTier();
  const { isPreloading } = usePreloading();
  const { quality } = useConnectionSpeed();

  // Memoize logo images array with quality-based paths
  const logoImages = useMemo(() => [
    getLogoPath('logo-1.svg', quality),
    getLogoPath('logo-2.svg', quality),
    getLogoPath('logo-3.svg', quality),
    getLogoPath('logo-4.svg', quality),
    getLogoPath('logo-5.svg', quality),
    getLogoPath('logo-6.svg', quality),
    getLogoPath('logo-7.svg', quality),
    getLogoPath('logo-8.svg', quality),
    getLogoPath('logo-9.svg', quality),
    getLogoPath('logo-10.svg', quality),
    getLogoPath('logo-11.svg', quality),
    getLogoPath('logo-12.svg', quality),
    getLogoPath('logo-13.svg', quality),
    getLogoPath('logo-14.svg', quality),
    getLogoPath('logo-15.svg', quality)
  ], [quality]);

  // Render images immediately when component mounts
  useEffect(() => {
    // If images are already rendered, don't check again
    if (imagesRenderedRef.current) {
      return;
    }

    // Render images immediately - they'll load when img elements are created
    setImagesReady(true);
    imagesRenderedRef.current = true;
    logger.debug('✅ Rendering logo images');
  }, []);

  // Memoize logo rotation interval based on performance settings
  const logoRotationInterval = useMemo(() => {
    // Adjust rotation speed based on GPU tier
    switch (performanceSettings.animationQuality) {
      case 'low': return 500; // Slower rotation for low-end devices
      case 'medium': return 400;
      case 'high': 
      default: return 300;
    }
  }, [performanceSettings.animationQuality]);

  // Optimized logo rotation with performance-based interval
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentLogoIndex((prevIndex) =>
        (prevIndex + 1) % logoImages.length
      );
    }, logoRotationInterval);

    return () => clearInterval(interval);
  }, [logoImages.length, logoRotationInterval]);


  // Update position when offset changes
  useEffect(() => {
    if (logoRef.current && logoRef.current.position) {
      logoRef.current.position.set(...offsetPosition);
    }
  }, [offsetPosition]);

  // Fade in logos when preloader finishes
  useEffect(() => {
    if (!isPreloading) {
      // Start fading in immediately when preloader state changes
      // The timing is coordinated with preloader fade-out in Preloader.jsx
      setOpacity(1);
    } else {
      // Keep hidden while preloading
      setOpacity(0);
    }
  }, [isPreloading]);

  // Optimized floating animation - only runs when logo is visible and with reduced frequency
  useConditionalFrame((state) => {
    if (logoRef.current && logoRef.current.position && typeof logoRef.current.position.y === 'number') {
      // Apply floating animation on top of the offset position
      const baseY = offsetPosition[1];
      logoRef.current.position.y = baseY + Math.sin(state.clock.elapsedTime * 0.5) * 0.1;
    }
  }, [offsetPosition], {
    maxFPS: Math.min(performanceSettings.maxFPS, 30), // Cap at 30fps for floating animation
    pauseWhenHidden: true, // Pause when page is hidden
    reduceFPSWhenHidden: true,
    hiddenFPS: 5
  });

  // Memoize the image styles to prevent re-creation (removed drop shadow)
  const imageStyles = useMemo(() => ({
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    position: 'absolute',
    top: 0,
    left: 0,
  }), []);

  const htmlStyles = useMemo(() => ({
    width: '634px',
    height: '634px',
    pointerEvents: 'none',
    opacity: opacity,
    transition: 'opacity 1.5s cubic-bezier(0.16, 1, 0.3, 1)',
    position: 'relative',
  }), [opacity]);

  return (
    <group>
      <Html
        ref={logoRef}
        position={offsetPosition}
        center
        distanceFactor={8}
        transform
        sprite
        style={htmlStyles}
      >
        {/* Render all logos - they'll load when img elements are created */}
        {imagesReady && logoImages.map((logoSrc, index) => {
          return (
            <img
              key={logoSrc}
              ref={(el) => {
                if (el) {
                  imgRefsRef.current.set(logoSrc, el);
                } else {
                  imgRefsRef.current.delete(logoSrc);
                }
              }}
              src={logoSrc}
              alt={`New Work City Logo ${index + 1}`}
              style={{
                ...imageStyles,
                display: index === currentLogoIndex ? 'block' : 'none',
                // No transition - instant hard switch to avoid flicker
              }}
            />
          );
        })}
      </Html>
    </group>
  );
};

// Custom memo comparison - only re-render if offsetPosition changes
export default memo(AnimatedLogos, (prevProps, nextProps) => {
  return JSON.stringify(prevProps.offsetPosition) === JSON.stringify(nextProps.offsetPosition);
});
